import { useEffect, useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight, Package, Upload } from 'lucide-react';
import { MenuType } from '../../services/menuTypes.service';
import { Category } from '../../hooks/useCategories';

interface MenuTypesSectionProps {
  menuTypes: MenuType[];
  allCategories: Category[];
  loading: boolean;
  focusedMenuTypeId?: string;
  focusedCategoryId?: string;
  onFocusedMenuTypeHandled?: () => void;
  onAddMenuType: () => void;
  onDeleteMenuType: (id: string, name: string) => void;
  onBulkUploadProducts: (menuTypeId: string) => void;
  onAddCategory: (menuTypeId: string) => void;
  onDeleteCategory: (id: string, name: string) => void;
  onViewCategoryItems?: (categoryId: string, categoryName: string, menuTypeName?: string, menuTypeId?: string) => void;
}

export function MenuTypesSection({
  menuTypes,
  allCategories,
  loading,
  focusedMenuTypeId,
  focusedCategoryId,
  onFocusedMenuTypeHandled,
  onAddMenuType,
  onDeleteMenuType,
  onBulkUploadProducts,
  onAddCategory,
  onDeleteCategory,
  onViewCategoryItems,
}: MenuTypesSectionProps) {
  const [expandedMenuTypes, setExpandedMenuTypes] = useState<Set<string>>(new Set());

  useEffect(() => {
    const categoryForFocus = focusedCategoryId
      ? allCategories.find(cat => cat.id === focusedCategoryId)
      : null;
    const resolvedMenuTypeId = focusedMenuTypeId || categoryForFocus?.menuTypeId;

    if (!resolvedMenuTypeId) {
      return;
    }

    setExpandedMenuTypes(prev => {
      const next = new Set(prev);
      next.add(resolvedMenuTypeId);
      return next;
    });

    window.setTimeout(() => {
      const categoryElement = focusedCategoryId
        ? document.getElementById(`menu-category-${focusedCategoryId}`)
        : null;

      if (categoryElement) {
        categoryElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        const menuTypeElement = document.getElementById(`menu-type-${resolvedMenuTypeId}`);
        if (menuTypeElement) {
          menuTypeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }

      onFocusedMenuTypeHandled?.();
    }, 60);
  }, [focusedMenuTypeId, focusedCategoryId, allCategories, onFocusedMenuTypeHandled]);

  const toggleMenuType = (menuTypeId: string) => {
    setExpandedMenuTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(menuTypeId)) {
        newSet.delete(menuTypeId);
      } else {
        newSet.add(menuTypeId);
      }
      return newSet;
    });
  };

  const getCategoriesForMenuType = (menuTypeId: string) => {
    return allCategories.filter(cat => cat.menuTypeId === menuTypeId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">Loading menu structure...</p>
        </div>
      </div>
    );
  }

  if (menuTypes.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-12 text-center">
        <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Menu Types Yet</h3>
        <p className="text-gray-500 dark:text-gray-400 mb-6">Get started by creating your first menu type (e.g., Breakfast, Lunch, Dinner)</p>
        <button
          onClick={onAddMenuType}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create First Menu Type
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {menuTypes.map((menuType) => {
        const categories = getCategoriesForMenuType(menuType.id);
        const isExpanded = expandedMenuTypes.has(menuType.id);

        return (
          <div
            key={menuType.id}
            id={`menu-type-${menuType.id}`}
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden"
          >
            {/* Menu Type Header */}
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  <button
                    onClick={() => toggleMenuType(menuType.id)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    )}
                  </button>
                  <div className="w-14 h-14 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl flex items-center justify-center text-3xl flex-shrink-0">
                    {menuType.icon || '🍽️'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{menuType.name}</h3>
                    {menuType.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{menuType.description}</p>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {categories.length} {categories.length === 1 ? 'category' : 'categories'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onBulkUploadProducts(menuType.id)}
                    className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors text-sm font-medium"
                  >
                    <Upload className="w-4 h-4" />
                    Bulk Upload
                  </button>
                  <button
                    onClick={() => onAddCategory(menuType.id)}
                    className="flex items-center gap-2 px-3 py-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors text-sm font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    Add Category
                  </button>
                  <button
                    onClick={() => onDeleteMenuType(menuType.id, menuType.name)}
                    className="flex items-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors text-sm font-medium"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Categories List (Expanded) */}
            {isExpanded && (
              <div className="border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 p-6">
                {categories.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      No categories yet for {menuType.name}
                    </p>
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => onBulkUploadProducts(menuType.id)}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        Bulk Upload
                      </button>
                      <button
                        onClick={() => onAddCategory(menuType.id)}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Add Manually
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {categories.map((category) => (
                      <div
                        key={category.id}
                        id={`menu-category-${category.id}`}
                        className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-100 dark:border-gray-800 hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-700 transition-all cursor-pointer group"
                        onClick={() => onViewCategoryItems?.(category.id, category.name, menuType.name, menuType.id)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 dark:text-white truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                              {category.name}
                            </h4>
                            {category.description && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                                {category.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                            {category._count?.products || 0} items
                          </span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteCategory(category.id, category.name);
                              }}
                              className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 rounded-md transition-colors"
                              title="Delete category"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}